// ── RegisterPage.jsx ──────────────────────────────────
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { authAPI, businessAPI } from '../../services/api';
import { setCredentials } from '../../store/slices/authSlice';
import { Button, Input, Select, Tabs } from '../../components/common/UI';
import logoImg from '../../assets/logo.png';
import toast from 'react-hot-toast';

import GoogleLoginButton from '../../components/auth/GoogleLoginButton';

const ACCOUNT_TABS = [
  { value: 'customer', label: 'Customer' },
  { value: 'business', label: 'Business Owner' },
];

export default function RegisterPage() {
  const [tab, setTab] = useState('customer');
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [userId, setUserId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate  = useNavigate();
  const dispatch  = useDispatch();

  const { register, handleSubmit, getValues, formState: { errors } } = useForm();
  const { register: regBiz, handleSubmit: hBiz, formState: { errors: bizErrors } } = useForm();

  const onRegister = async (data) => {
    setLoading(true);
    try {
      const res = await authAPI.registerEmail({ ...data, role: tab === 'business' ? 'business_owner' : 'customer' });
      const { user, accessToken, refreshToken } = res.data.data;
      dispatch(setCredentials({ user, accessToken, refreshToken }));
      setUserId(user.id);
      if (tab === 'business') { setStep(2); }
      else { toast.success('Account created!'); navigate('/'); }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Registration failed');
    } finally { setLoading(false); }
  };

  const onCreateBusiness = async (data) => {
    setLoading(true);
    try {
      await businessAPI.create(data);
      console.log('Business created, pending approval', data);
      toast.success('Business registered! Pending admin approval.');
      navigate('/business');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to create business');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen flex gradient-mesh">
      <div className="hidden lg:flex lg:w-1/2 flex-col justify-center items-center p-16 relative overflow-hidden">
        <div className="absolute inset-0 gradient-brand opacity-95" />
        <div className="relative z-10 text-white text-center max-w-sm">
          <div className="mb-6 flex items-center justify-center">
            <img src={logoImg} alt="Bellezbuy Admin" className="h-20 w-20 object-contain drop-shadow-xl" />
          </div>
          <h1 className="font-display text-4xl font-bold mb-4">Join Bellezbuy Admin</h1>
          <p className="text-white/80 text-lg">Start selling to thousands of customers today.</p>
          <div className="mt-10 space-y-4 text-left">
            {[
              ['Free to join', 'No setup fees — pay only when you sell'],
              ['Fast payouts',  'Direct Razorpay transfers to your bank'],
              ['Easy management','Powerful dashboard for orders and inventory'],
            ].map(([t, d]) => (
              <div key={t} className="flex gap-3 rounded-2xl bg-white/10 p-4">
                <span className="text-xl">✓</span>
                <div><p className="font-semibold">{t}</p><p className="text-sm text-white/70">{d}</p></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex w-full lg:w-1/2 flex-col justify-center items-center p-8">
        <div className="w-full max-w-[440px]">
          <div className="flex items-center gap-3 mb-8">
            <img src={logoImg} alt="Bellezbuy Admin" className="h-9 w-9 object-contain" />
            <span className="font-display font-bold text-surface-900 text-lg">Bellezbuy Admin</span>
          </div>

          {step === 1 ? (
            <>
              <h2 className="font-display text-2xl font-bold text-surface-900 mb-1">Create account</h2>
              <p className="text-sm text-surface-500 mb-6">Get started in minutes</p>

              <div className="mb-6"><Tabs tabs={ACCOUNT_TABS} active={tab} onChange={setTab} /></div>

              <GoogleLoginButton buttonText="Sign up with Google" />

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-surface-200" /></div>
                <div className="relative flex justify-center"><span className="bg-surface-50 px-3 text-xs text-surface-400">or sign up with</span></div>
              </div>

              <form onSubmit={handleSubmit(onRegister)} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <Input label="First name" error={errors.first_name?.message} {...register('first_name', { required: 'Required' })} />
                  <Input label="Last name" {...register('last_name')} />
                </div>
                <Input label="Email" type="email" error={errors.email?.message}
                  {...register('email', { required: 'Email required', pattern: { value: /\S+@\S+\.\S+/, message: 'Invalid email' } })} />
                <Input label="Phone" type="tel" {...register('phone')} placeholder="+91 98765 43210" />
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  error={errors.password?.message}
                  suffix={
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="focus:outline-none focus:text-brand-600 transition-colors"
                    >
                      {showPassword ? (
                        <svg className="h-5 w-5 text-surface-400 hover:text-surface-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" />
                        </svg>
                      ) : (
                        <svg className="h-5 w-5 text-surface-400 hover:text-surface-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                        </svg>
                      )}
                    </button>
                  }
                  {...register('password', { required: 'Required', minLength: { value: 8, message: 'Min 8 characters' } })}
                />
                <Button type="submit" variant="primary" size="lg" className="w-full" loading={loading}>
                  {tab === 'business' ? 'Continue to Business Setup →' : 'Create Account'}
                </Button>
              </form>
            </>
          ) : (
            <>
              <h2 className="font-display text-2xl font-bold text-surface-900 mb-1">Business details</h2>
              <p className="text-sm text-surface-500 mb-6">Set up your seller profile</p>

              <form onSubmit={hBiz(onCreateBusiness)} className="space-y-4">
                <Input label="Business Name *" error={bizErrors.name?.message} {...regBiz('name', { required: 'Required' })} />
                <Input label="Business Type" {...regBiz('business_type')} placeholder="e.g. Electronics, Clothing, Food" />
                <div className="grid grid-cols-2 gap-3">
                  <Input label="GST Number" {...regBiz('gst_number')} placeholder="22AAAAA0000A1Z5" />
                  <Input label="PAN Number" {...regBiz('pan_number')} />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Input label="City" {...regBiz('city')} />
                  <Input label="State" {...regBiz('state')} />
                </div>
                <Input label="Pincode" {...regBiz('pincode')} />
                <Button type="submit" variant="primary" size="lg" className="w-full" loading={loading}>
                  Register Business
                </Button>
              </form>
            </>
          )}

          <p className="mt-6 text-center text-xs text-surface-500">
            Already have an account?{' '}
            <Link to="/login" className="text-brand-600 font-medium hover:underline">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
